import { supabase } from '../supabase.js';
import { env } from '../env.js';

export interface ApiError extends Error {
  status: number;
  code?: string;
  details?: unknown;
}

function asError(status: number, body: unknown): ApiError {
  const e = new Error(
    (body as { error?: { message?: string } })?.error?.message ?? `HTTP ${status}`,
  ) as ApiError;
  e.status = status;
  const errBody = (body as { error?: { code?: string; details?: unknown } })?.error;
  if (errBody?.code) e.code = errBody.code;
  if (errBody?.details) e.details = errBody.details;
  return e;
}

export interface CallOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  responseType?: 'json' | 'blob' | 'text';
}

export async function callFunction<T = unknown>(name: string, opts: CallOptions = {}): Promise<T> {
  const url = new URL(`${env.supabaseUrl}/functions/v1/${name}`);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  }
  const { data: sess } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    apikey: env.supabaseAnonKey,
    authorization: `Bearer ${sess.session?.access_token ?? env.supabaseAnonKey}`,
  };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(url, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body,
    signal: opts.signal,
  });
  if (opts.responseType === 'blob') {
    if (!res.ok) throw asError(res.status, await res.json().catch(() => null));
    return (await res.blob()) as T;
  }
  if (opts.responseType === 'text') {
    if (!res.ok) throw asError(res.status, await res.text());
    return (await res.text()) as T;
  }
  const json = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) throw asError(res.status, json);
  return json as T;
}
